import {
  Button,
  EmptyState,
  FlexBox,
  FormField,
  FormGrid,
  Icon,
  Page,
  Spinner,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { useForm } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';

import { trustpilotApi } from 'apis/trustpilot/trustpilot.api';
import { AppLink } from 'components/appLink';
import { HookFormQueuesSelect } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { OAuthButton } from '../../components/oAuthButton/oAuthButton';
import { TrustpilotScorePicker } from '../../components/trustpilotScorePicker/trustpilotScorePicker';

const formId = 'settings-reviews-edit-form';

type SettingsReviewsFormValues = {
  score_filter?: number;
  default_queue_id: string[];
};

export const SettingsReviewsEditView = (): JSX.Element => {
  const { id } = useAppPathParams('/settings/reviews/:id');
  const notify = useNotifications();
  const navigate = useAppNavigate();

  const [updateTrustpilotIntegration] =
    trustpilotApi.useUpdateTrustpilotIntegrationMutation();

  const {
    integration,
    isFetching: isIntegrationFetching,
    refetch,
  } = trustpilotApi.useReadTrustpilotIntegrationQuery(
    { id },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        integration: data?.data,
      }),
    },
  );

  const defaultValues = {
    default_queue_id: integration?.default_queue_id
      ? [integration.default_queue_id]
      : [],
    score_filter: integration?.score_filter?.max,
  };

  const form = useForm<SettingsReviewsFormValues>({
    mode: 'all',
    defaultValues,
  });

  const scoreFilter = form.watch('score_filter');

  const handleSubmit = async ({
    default_queue_id,
    score_filter,
  }: SettingsReviewsFormValues) => {
    if (!integration) {
      return;
    }

    const response = await updateTrustpilotIntegration({
      default_queue_id: default_queue_id.at(0),
      score_filter: score_filter ? { max: score_filter } : undefined,
      id: integration.id,
    });

    if (isResponseWithData(response)) {
      notify(
        {
          title: 'Trustpilot review domain updated',
          text: `Changes to the "${integration?.identifying_domain}" domain have been saved.`,
          icon: 'i-done',
          time: 3000,
        },
        NotificationType.Success,
      );

      navigate({ path: '/settings/reviews' });
    } else {
      notify(
        {
          title: 'Error while updating the Trustpilot review domain.',
        },
        NotificationType.Error,
      );
    }
  };

  const handleCancel = () => form.reset(defaultValues);

  useUpdateEffect(() => {
    form.reset(defaultValues);
    // Run only when integration changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integration]);

  const renderFormSection = () => {
    if (!integration) {
      return (
        <FlexBox alignItems="center" justifyContent="center" padding="500 0">
          {isIntegrationFetching ? (
            <Spinner size={24} />
          ) : (
            <EmptyState
              title="Could not retrieve the review domain"
              description="Trustpilot review domain might not exist or it could be a result of an error"
              buttonProps={{
                children: 'Try again',
                onClick: () => refetch(),
              }}
            />
          )}
        </FlexBox>
      );
    }

    return (
      <Page.Section
        title="Edit review domain"
        controls={
          <>
            <Button
              variant="secondary"
              disabled={!form.formState.isDirty || form.formState.isSubmitting}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={form.formState.isSubmitting}
              disabled={
                !form.formState.isDirty ||
                !form.formState.isValid ||
                form.formState.isSubmitting
              }
              form={formId}
            >
              Save changes
            </Button>
          </>
        }
      >
        <form
          id={formId}
          noValidate
          tabIndex={-1}
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <FormGrid>
            <FormField span={4}>
              <HookFormQueuesSelect
                name="default_queue_id"
                label="Default queue for incoming reviews"
                control={form.control}
                rules={{
                  required: 'Queue is required',
                }}
              />
            </FormField>

            <OAuthButton integration={integration} />

            <TrustpilotScorePicker
              selected={scoreFilter}
              onSelect={(value) => {
                form.setValue('score_filter', value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
            />
          </FormGrid>
        </form>
      </Page.Section>
    );
  };

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            <AppLink path="/settings/reviews" style={{ color: 'inherit' }}>
              Reviews
            </AppLink>

            {integration?.identifying_domain && (
              <>
                <Icon name="angle-right" />
                {integration.identifying_domain}
              </>
            )}
          </FlexBox>
        }
      />
      <Page.Body>{renderFormSection()}</Page.Body>
    </Page>
  );
};
